import React from "react";
import MuiDialog from "@mui/material/Dialog";
import type { DialogProps } from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import type { SxProps, Theme } from "@mui/material/styles";

import type { ModalAnalyticsId } from "@/analytics/modalIds";
import { useModalAnalytics } from "@/analytics/useModalAnalytics";

export interface ModalProps {
  /** Names this modal in analytics. Required so a new one cannot ship
   * untracked — see `src/analytics/modalIds.ts` for why it can't be derived
   * from the title. */
  analyticsId: ModalAnalyticsId;
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  titleAlignment?: "center" | "left";
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
  maxWidth?: DialogProps["maxWidth"];
  /** False hides the close (X) and blocks Escape/backdrop dismissal, for
   * mandatory steps (e.g. a required signup step) that shouldn't be
   * dismissible until the caller advances them. Default true. */
  dismissible?: boolean;
  /** Forwarded straight to MuiDialog — for the rare case that needs the
   * Paper itself (position, margin, max-width, aria-label), which `sx`
   * (scoped to the inner content Box) can't reach. */
  slotProps?: DialogProps["slotProps"];
  /** MUI's default: a backdrop click closes, same as Escape. Set false to
   * require an explicit action — Escape still closes either way. Mirrors
   * Dialog's prop of the same name. */
  closeOnBackdropClick?: boolean;
}

/**
 * The generic container half of the app's two shared overlays: arbitrary
 * content, caller-owned footer, a close (X). For a fixed title+message+
 * confirm/cancel shape, use `Dialog` instead.
 */
const Modal: React.FC<ModalProps> = ({
  analyticsId,
  open,
  onClose,
  title,
  children,
  sx,
  titleAlignment = "center",
  fullWidth = false,
  maxWidth = false,
  dismissible = true,
  slotProps,
  closeOnBackdropClick = true,
}) => {
  const titleId = React.useId();
  const { reportClose } = useModalAnalytics({ analyticsId, surface: "modal", open });

  const handleClose = (
    _event: unknown,
    reason?: "backdropClick" | "escapeKeyDown",
  ) => {
    // Reported only past the guards: a dismissal the modal refuses did not
    // close it, and recording one would invent a close that never happened.
    if (!dismissible) return;
    if (reason === "backdropClick" && !closeOnBackdropClick) return;
    // Modal has no confirm action, so every close a user drives is a dismissal.
    reportClose("dismissed", reason === "backdropClick" ? "backdrop" : "escape");
    onClose();
  };

  return (
    <MuiDialog
      open={open}
      onClose={handleClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      aria-labelledby={typeof title === "string" ? titleId : undefined}
      slotProps={slotProps}
    >
      <Box sx={{ p: 4, ...sx }}>
        <Stack direction="row" sx={{ alignItems: "flex-start", mb: 3 }}>
          {typeof title === "string" ? (
            <Typography
              id={titleId}
              variant="h1"
              sx={{
                fontWeight: "bold",
                width: "100%",
                textAlign: "center",
                pl: 6,
                pr: 1,
              }}
            >
              {title}
            </Typography>
          ) : (
            <Box
              sx={{
                width: "100%",
                pl: titleAlignment === "center" ? 5 : 0,
                pr: 1,
              }}
            >
              {title}
            </Box>
          )}
          {dismissible && (
            <IconButton
              aria-label="close"
              onClick={(event) => {
                // Modal is sometimes a sibling inside a clickable ancestor
                // (e.g. an avatar wrapper that opens it) — React's synthetic
                // events bubble through the component tree even across a
                // portal, so without this the ancestor's onClick re-fires.
                event.stopPropagation();
                reportClose("dismissed", "close_button");
                onClose();
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          )}
        </Stack>
        {children}
      </Box>
    </MuiDialog>
  );
};

export default Modal;
